import baseAPI from '@/api';
import { UserQueryKeys } from '@/api/user/queryKeys';
import { CookiesKey } from '@/enums/cookiesKey';
import { getQueryClient } from '@/lib/providers/TanstackQueryProvider';
import { getStore } from '@/lib/store';
import { setGameMode } from '@/lib/store/casino/actions';
import { CasinoGameMode } from '@/lib/store/casino/props';
import { deleteCookie, getCookie, hasCookie, setCookie } from 'cookies-next';
import { LoginByEmailSuccessOutputDto } from '../dto/auth/auth-login-response.dto';

export class AuthService {
  static isAuthorized(): boolean {
    return ((hasCookie(CookiesKey.Token) &&
      hasCookie(CookiesKey.RefreshToken)) ||
      !!getQueryClient().getQueryData([UserQueryKeys.Profile])) as boolean;
  }

  static setAsAuthorized(data?: LoginByEmailSuccessOutputDto): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (data) {
      AuthService.setToken(data.accessToken);
      AuthService.setRefreshToken(data.refreshToken);
    }
  }

  static tempSetAsUnauthorized(): void {
    if (typeof window === 'undefined') {
      return;
    }

    // Save temp tokens
    setCookie(CookiesKey.TempToken, getCookie(CookiesKey.Token) as string, {
      maxAge:
        Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
      secure: true,
      sameSite: 'strict',
    });
    setCookie(
      CookiesKey.TempRefreshToken,
      getCookie(CookiesKey.RefreshToken) as string,
      {
        maxAge:
          Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
        secure: true,
        sameSite: 'strict',
      },
    );

    deleteCookie(CookiesKey.Token);
    deleteCookie(CookiesKey.RefreshToken);
    getQueryClient()?.removeQueries?.();
  }

  static restoreTempTokens(): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(CookiesKey.Token, getCookie(CookiesKey.TempToken) as string, {
      maxAge:
        Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
      secure: true,
      sameSite: 'strict',
    });
    setCookie(
      CookiesKey.RefreshToken,
      getCookie(CookiesKey.TempRefreshToken) as string,
      {
        maxAge:
          Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
        secure: true,
        sameSite: 'strict',
      },
    );

    deleteCookie(CookiesKey.TempToken);
    deleteCookie(CookiesKey.TempRefreshToken);

    getQueryClient().invalidateQueries();
  }

  static setHasKyc(hasKyc: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(CookiesKey.HasKyc, hasKyc.toString(), {
      maxAge:
        Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
      secure: true,
      sameSite: 'strict',
    });
  }

  static setAsUnauthorized(): void {
    deleteCookie(CookiesKey.Token);
    deleteCookie(CookiesKey.RefreshToken);
  }

  static setToken(token: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(CookiesKey.Token, token, {
      maxAge:
        Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
      secure: true,
      sameSite: 'strict',
    });
    baseAPI.defaults.headers['authorization'] = token;
  }
  static setRefreshToken(token: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(CookiesKey.RefreshToken, token, {
      maxAge:
        Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
      secure: true,
      sameSite: 'strict',
    });
    baseAPI.defaults.headers['x-refresh-token'] = token;
  }

  static setVoucher(voucher: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    setCookie(CookiesKey.Voucher, voucher, {
      maxAge:
        Number(process.env.NEXT_PUBLIC_JWT_EXPIRE_SECONDS as string) ?? 1800,
      secure: true,

      sameSite: 'strict',
    });
  }

  static removeVoucher() {
    if (typeof window === 'undefined') {
      return;
    }

    deleteCookie(CookiesKey.Voucher);
  }

  static hasVoucher(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return hasCookie(CookiesKey.Voucher) as boolean;
  }

  static getVoucher(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return getCookie(CookiesKey.Voucher) as string;
  }

  static setBetbyToken(token: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(CookiesKey.BetbyToken, token);
  }

  static getBetbyToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    return localStorage.getItem(CookiesKey.BetbyToken) as string;
  }

  static removeBetbyToken(): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(CookiesKey.BetbyToken);
  }

  static hasBetbyToken(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return localStorage.getItem(CookiesKey.BetbyToken) !== null;
  }

  static refreshAuthorization() {
    if (!AuthService.isAuthorized()) {
      return;
    }

    AuthService.setAsAuthorized();

    if (hasCookie(CookiesKey.Token)) {
      AuthService.setToken(getCookie(CookiesKey.Token) as string);
    }

    if (hasCookie(CookiesKey.RefreshToken)) {
      AuthService.setRefreshToken(getCookie(CookiesKey.RefreshToken) as string);
    }
  }

  static logOut(): void {
    AuthService.setAsUnauthorized();
    deleteCookie(CookiesKey.HideBonusCardMobile);
    deleteCookie(CookiesKey.LastUserBonusId);
    if (typeof window != 'undefined') {
      getStore().dispatch(setGameMode(CasinoGameMode.Demo));
      getQueryClient().removeQueries();
    }
  }
}
